/**
 * Payment method definitions — types and display data only.
 * No platform-specific validation or QR code generation.
 */

import type { PaymentMethodType, PaymentMethodCategory, PaymentMethod } from '../types';

export type { PaymentMethodType, PaymentMethodCategory, PaymentMethod };

export interface PaymentMethodDefinition {
  type: PaymentMethodType;
  name: string;
  shortName: string;
  icon: string;
  category: PaymentMethodCategory;
  countries: string[];
}

export const PAYMENT_METHOD_DEFINITIONS: PaymentMethodDefinition[] = [
  { type: 'czech_bank', name: 'Czech Bank Account', shortName: 'CZ Bank', icon: 'credit-card', category: 'bank_transfer', countries: ['CZ'] },
  { type: 'iban', name: 'IBAN (International)', shortName: 'IBAN', icon: 'credit-card', category: 'bank_transfer', countries: [] },
  { type: 'uk_bank', name: 'UK Bank Account', shortName: 'UK Bank', icon: 'credit-card', category: 'bank_transfer', countries: ['GB'] },
  { type: 'us_bank', name: 'US Bank Account (ACH)', shortName: 'US Bank', icon: 'credit-card', category: 'bank_transfer', countries: ['US'] },
  { type: 'au_bank', name: 'Australian Bank Account', shortName: 'AU Bank', icon: 'credit-card', category: 'bank_transfer', countries: ['AU'] },
  { type: 'paypal', name: 'PayPal', shortName: 'PayPal', icon: 'wallet', category: 'digital_wallet', countries: [] },
  { type: 'revolut', name: 'Revolut', shortName: 'Revolut', icon: 'smartphone', category: 'digital_wallet', countries: [] },
  { type: 'wise', name: 'Wise', shortName: 'Wise', icon: 'arrow-left-right', category: 'digital_wallet', countries: [] },
  { type: 'venmo', name: 'Venmo', shortName: 'Venmo', icon: 'smartphone', category: 'digital_wallet', countries: ['US'] },
  { type: 'zelle', name: 'Zelle', shortName: 'Zelle', icon: 'zap', category: 'digital_wallet', countries: ['US'] },
  { type: 'cashapp', name: 'Cash App', shortName: 'Cash App', icon: 'banknote', category: 'digital_wallet', countries: ['US', 'GB'] },
  { type: 'upi', name: 'UPI', shortName: 'UPI', icon: 'smartphone', category: 'mobile_payment', countries: ['IN'] },
  { type: 'pix', name: 'PIX', shortName: 'PIX', icon: 'qr-code', category: 'mobile_payment', countries: ['BR'] },
  { type: 'mbway', name: 'MB WAY', shortName: 'MB WAY', icon: 'smartphone', category: 'mobile_payment', countries: ['PT'] },
  { type: 'bizum', name: 'Bizum', shortName: 'Bizum', icon: 'smartphone', category: 'mobile_payment', countries: ['ES'] },
  { type: 'swish', name: 'Swish', shortName: 'Swish', icon: 'smartphone', category: 'mobile_payment', countries: ['SE'] },
  { type: 'google_pay', name: 'Google Pay', shortName: 'GPay', icon: 'smartphone', category: 'mobile_payment', countries: [] },
  { type: 'apple_pay', name: 'Apple Pay', shortName: 'Apple Pay', icon: 'smartphone', category: 'mobile_payment', countries: [] },
  { type: 'phone', name: 'Phone Number', shortName: 'Phone', icon: 'phone', category: 'mobile_payment', countries: [] },
  { type: 'other', name: 'Other', shortName: 'Other', icon: 'more-horizontal', category: 'digital_wallet', countries: [] },
];

const definitionsByType = new Map<PaymentMethodType, PaymentMethodDefinition>(
  PAYMENT_METHOD_DEFINITIONS.map(d => [d.type, d])
);

export const getPaymentMethodDefinition = (
  type: PaymentMethodType
): PaymentMethodDefinition | undefined => definitionsByType.get(type);

// Category headings are localized via profile:paymentMethods.category.<category>.
